import { addMonths, format } from "date-fns";
import { type FormEvent, useState } from "react";
import { useCreateRecurringReservation } from "../../hooks/api/useReservations";
import { DAY_OPTIONS } from "../../lib/day-of-week";
import { useToastStore } from "../../stores/toastStore";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";

interface RecurringReservationFormProps {
  seatId: number;
  seatLabel: string;
  defaultStartDate: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

const DAY_BUTTON_BASE = [
  "inline-flex h-10 w-10 items-center justify-center rounded-full",
  "text-sm font-medium transition-colors duration-200",
  "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
].join(" ");

function getDayButtonClassName(isSelected: boolean): string {
  const variant = isSelected
    ? "bg-primary-600 text-white"
    : "bg-gray-100 text-gray-700 hover:bg-gray-200";
  return `${DAY_BUTTON_BASE} ${variant}`;
}

export function RecurringReservationForm({
  seatId,
  seatLabel,
  defaultStartDate,
  onSuccess,
  onCancel,
}: RecurringReservationFormProps) {
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState("");
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const createRecurring = useCreateRecurringReservation();
  const addToast = useToastStore((s) => s.addToast);

  const today = format(new Date(), "yyyy-MM-dd");
  const maxEndDate = format(
    addMonths(new Date(startDate || today), 6),
    "yyyy-MM-dd"
  );

  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await createRecurring.mutateAsync({
        startDate,
        endDate: endDate || undefined,
        daysOfWeek: selectedDays,
        seatId,
      });

      // Issue #102: 理由別集計で表示し、全件スキップ時は警告に切り替える。
      const seatTakenCount = result.skippedDates.filter(
        (s) => s.reason === "seat_taken"
      ).length;
      const userBookedCount = result.skippedDates.filter(
        (s) => s.reason === "user_already_booked"
      ).length;

      // 全件スキップ: 成功トーストではなく setError で警告表示し、フォームを維持する。
      if (result.createdCount === 0) {
        setError(
          buildAllSkippedMessage(result.skippedCount, {
            seatTakenCount,
            userBookedCount,
          })
        );
        return;
      }

      // 一部成功 or 全成功
      if (result.skippedCount > 0) {
        const parts = [`${result.createdCount}件作成`];
        if (seatTakenCount > 0) {
          parts.push(`${seatTakenCount}件は他の人が予約済み`);
        }
        if (userBookedCount > 0) {
          parts.push(`${userBookedCount}件はあなたが同日に別座席を予約済み`);
        }
        addToast(
          `${seatLabel}の繰り返し予約を作成しました（${parts.join(" / ")}）`
        );
      } else {
        addToast(
          `${seatLabel}の繰り返し予約を作成しました（${result.createdCount}件）`
        );
      }
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err, "繰り返し予約の作成に失敗しました"));
    }
  };

  /**
   * 全件スキップ時のエラーメッセージを組み立てる。
   * 理由の内訳を文言に含め、「予約できなかった件数」と「何をすべきか」を
   * ユーザーがすぐに把握できるようにする。
   */
  function buildAllSkippedMessage(
    totalSkipped: number,
    counts: { seatTakenCount: number; userBookedCount: number }
  ): string {
    const reasonParts: string[] = [];
    if (counts.seatTakenCount > 0) {
      reasonParts.push(`${counts.seatTakenCount}件は他の人が予約済み`);
    }
    if (counts.userBookedCount > 0) {
      reasonParts.push(
        `${counts.userBookedCount}件はあなたが同日に別座席を予約済み`
      );
    }
    const suffix =
      reasonParts.length > 0 ? `（${reasonParts.join(" / ")}）` : "";
    return `繰り返し予約を作成できませんでした。${totalSkipped}件すべてがスキップされました${suffix}。条件を見直してください。`;
  }

  const isSubmitDisabled =
    selectedDays.length === 0 || createRecurring.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <dl className="space-y-3">
        <div className="flex">
          <dt className="w-20 text-gray-500 shrink-0">座席</dt>
          <dd className="text-gray-900 font-medium">{seatLabel}</dd>
        </div>
      </dl>

      <Input
        id="recurring-start-date"
        label="開始日"
        type="date"
        value={startDate}
        min={today}
        onChange={(e) => setStartDate(e.target.value)}
        required
      />

      <Input
        id="recurring-end-date"
        label="終了日（省略時: 3ヶ月後）"
        type="date"
        value={endDate}
        min={startDate || today}
        max={maxEndDate}
        onChange={(e) => setEndDate(e.target.value)}
      />

      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-2">
          曜日を選択
        </legend>
        <div className="flex flex-wrap gap-2">
          {DAY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => toggleDay(option.value)}
              aria-pressed={selectedDays.includes(option.value)}
              className={getDayButtonClassName(
                selectedDays.includes(option.value)
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="flex justify-end gap-3 pt-2">
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={createRecurring.isPending}
        >
          閉じる
        </Button>
        <Button
          variant="primary"
          type="submit"
          isLoading={createRecurring.isPending}
          disabled={isSubmitDisabled}
        >
          繰り返し予約を作成
        </Button>
      </div>
    </form>
  );
}
